import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/Card';
import Button from './ui/Button';
import { theme } from '../styles/design-system';
import {
  Brain,
  Activity,
  Zap,
  History,
  Lightbulb,
  Play,
  Square,
  RotateCcw,
  CheckCircle,
  XCircle,
  BarChart3,
  Cpu,
  TrendingUp,
  AlertTriangle,
  Settings
} from 'lucide-react';
import {
  getAgentsStatus,
  startParameterOptimization,
  stopParameterOptimization,
  getParameterRecommendations,
  getOptimizationHistory,
  applyParameterRecommendations,
  getIntelligenceSummary,
  triggerImmediateOptimization
} from '../services/apiClient';

const AgenticAIDashboard = () => {
  const { isAuthenticated, user } = useAuth();
  const { isDark } = useTheme();
  const [agentsStatus, setAgentsStatus] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [optimizationHistory, setOptimizationHistory] = useState([]);
  const [intelligenceSummary, setIntelligenceSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Load data on component mount
  useEffect(() => {
    loadAgentsData();
  }, []);

  const loadAgentsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusRes, recommendationsRes, historyRes, summaryRes] = await Promise.all([
        getAgentsStatus(),
        getParameterRecommendations(),
        getOptimizationHistory(10),
        getIntelligenceSummary()
      ]);

      setAgentsStatus(statusRes.data);
      setRecommendations(recommendationsRes.data);
      setOptimizationHistory(historyRes.data.history || []);
      setIntelligenceSummary(summaryRes.data);
    } catch (err) {
      setError('Failed to load AI agents data');
      console.error('Error loading agents data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOptimization = async () => {
    setLoading(true);
    try {
      await startParameterOptimization(300); // 5 minutes interval
      await loadAgentsData();
    } catch (err) {
      setError('Failed to start parameter optimization');
      console.error('Error starting optimization:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStopOptimization = async () => {
    setLoading(true);
    try {
      await stopParameterOptimization();
      await loadAgentsData();
    } catch (err) {
      setError('Failed to stop parameter optimization');
      console.error('Error stopping optimization:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyRecommendations = async () => {
    setLoading(true);
    try {
      await applyParameterRecommendations();
      await loadAgentsData();
    } catch (err) {
      setError('Failed to apply recommendations');
      console.error('Error applying recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerOptimization = async () => {
    setLoading(true);
    try {
      await triggerImmediateOptimization();
      await loadAgentsData();
    } catch (err) {
      setError('Failed to trigger optimization');
      console.error('Error triggering optimization:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${(value * 100).toFixed(2)}%`;
    }
    return value || 'N/A';
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.authPrompt}>
        <h2 style={styles.authTitle}>🔐 Authentication Required</h2>
        <p style={styles.authMessage}>
          Please login to access the Agentic AI Dashboard.
        </p>
      </div>
    );
  }

  const isOptimizationActive = agentsStatus?.agents?.parameter_optimization?.active;

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroTitle}>
            <Brain size={32} />
            <span>Agentic AI Dashboard</span>
          </div>
          <p style={styles.heroSubtitle}>
            AI-powered parameter optimization and intelligent trading recommendations
          </p>
        </div>
        <div style={styles.heroActions}>
          {isOptimizationActive ? (
            <Button
              onClick={handleStopOptimization}
              disabled={loading}
              variant="danger"
              icon={<Square size={16} />}
            >
              Stop Optimization
            </Button>
          ) : (
            <Button
              onClick={handleStartOptimization}
              disabled={loading}
              variant="secondary"
              icon={<Play size={16} />}
            >
              Start Optimization
            </Button>
          )}
          <Button
            onClick={loadAgentsData}
            variant="outline"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            icon={<RotateCcw size={16} />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.card, borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: theme.spacing[6] }}>
          <AlertTriangle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Custom Tabs */}
      <div style={styles.tabsContainer}>
        {['overview', 'recommendations', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab ? styles.activeTab : {})
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}>
          <div style={styles.dashboardGrid}>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>System Status</span>
                  <Activity size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {isOptimizationActive ? <span style={{ color: '#10b981' }}>Active</span> : <span style={{ color: '#6b7280' }}>Inactive</span>}
                </div>
                <div style={styles.metricSubtext}>
                  Last update: {formatTimestamp(agentsStatus?.timestamp)}
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Optimizations</span>
                  <Zap size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {intelligenceSummary?.ai_capabilities?.parameter_optimization?.optimization_count || 0}
                </div>
                <div style={styles.metricSubtext}>
                  Total optimization runs
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Learning</span>
                  <Brain size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {intelligenceSummary?.system_intelligence?.learning_enabled ? <span style={{ color: '#10b981' }}>Enabled</span> : <span style={{ color: '#ef4444' }}>Disabled</span>}
                </div>
                <div style={styles.metricSubtext}>
                  Continuous learning
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Adaptation</span>
                  <Cpu size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {intelligenceSummary?.system_intelligence?.adaptation_active ? <span style={{ color: '#10b981' }}>Active</span> : <span style={{ color: '#ef4444' }}>Inactive</span>}
                </div>
                <div style={styles.metricSubtext}>
                  Real-time adaptation
                </div>
              </CardContent>
            </Card>
          </div>

          <div style={styles.grid2}>
            <Card>
              <CardHeader>
                <CardTitle style={styles.sectionTitle}>
                  <Cpu size={20} /> System Intelligence
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Auto Optimization</span>
                  {intelligenceSummary?.system_intelligence?.auto_optimization ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Learning Enabled</span>
                  {intelligenceSummary?.system_intelligence?.learning_enabled ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Adaptation Active</span>
                  {intelligenceSummary?.system_intelligence?.adaptation_active ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Active Agents</span>
                  <span style={styles.infoValue}>{agentsStatus ? Object.keys(agentsStatus.agents || {}).length : 0}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle style={styles.sectionTitle}>
                  <Settings size={20} /> Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3] }}>
                  <Button
                    onClick={handleTriggerOptimization}
                    disabled={loading}
                    variant="outline"
                    icon={<Zap size={16} />}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    Trigger Immediate Optimization
                  </Button>
                  <Button
                    onClick={handleApplyRecommendations}
                    disabled={loading || !recommendations?.recommendations?.length}
                    variant="outline"
                    icon={<CheckCircle size={16} />}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    Apply All Recommendations
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <Card>
          <CardHeader>
            <CardTitle style={styles.sectionTitle}>
              <Lightbulb size={20} /> Parameter Recommendations
            </CardTitle>
            <CardDescription>AI-generated suggestions to improve performance</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div style={styles.emptyState}>Loading recommendations...</div>
            ) : !recommendations?.recommendations?.length ? (
              <div style={styles.emptyState}>No recommendations available at this time</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                {recommendations.recommendations.map((rec, index) => (
                  <div key={index} style={styles.cardItem}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardTitle}>
                        <Settings size={16} />
                        <span>{rec.parameter_name}</span>
                        <span style={{ ...styles.badge, ...styles.badgeBlue }}>{formatPercentage(rec.confidence)} Conf.</span>
                      </div>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: theme.spacing[3] }}>{rec.description}</p>
                    <div style={styles.cardGrid}>
                      <div>
                        <span style={styles.infoLabel}>Current Value</span>
                        <div style={{ ...styles.infoValue, fontFamily: 'monospace' }}>{rec.current_value}</div>
                      </div>
                      <div>
                        <span style={styles.infoLabel}>Recommended</span>
                        <div style={{ ...styles.infoValue, color: '#10b981', fontFamily: 'monospace' }}>{rec.recommended_value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle style={styles.sectionTitle}>
              <History size={20} /> Optimization History
            </CardTitle>
            <CardDescription>Recent optimization runs and results</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div style={styles.emptyState}>Loading history...</div>
            ) : !optimizationHistory.length ? (
              <div style={styles.emptyState}>No optimization history available</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                {optimizationHistory.map((item, index) => (
                  <div key={index} style={styles.cardItem}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardTitle}>
                        <Zap size={16} />
                        <span>{item.optimization_method}</span>
                      </div>
                      <span style={styles.infoLabel}>{formatTimestamp(item.timestamp)}</span>
                    </div>
                    <div style={styles.cardGrid}>
                      <div>
                        <span style={styles.infoLabel}>Improvement</span>
                        <div style={{ ...styles.infoValue, color: '#10b981' }}>+{formatPercentage(item.expected_improvement)}</div>
                      </div>
                      <div>
                        <span style={styles.infoLabel}>Confidence</span>
                        <div style={styles.infoValue}>{formatPercentage(item.confidence)}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: theme.spacing[3] }}>
                      <span style={styles.infoLabel}>Parameters Adjusted:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing[2], marginTop: theme.spacing[1] }}>
                        {Object.entries(item.parameters).map(([key, value]) => (
                          <span key={key} style={styles.parameterBadge}>
                            {key}: {value}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)', // Purple/Indigo gradient for AI
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[8],
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'white',
    opacity: 0.95,
    margin: '8px 0 0 0',
    fontWeight: theme.typography.fontWeight.medium,
    maxWidth: '600px',
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing[3],
    zIndex: 1,
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing[6],
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[2],
  },
  metricTitle: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  metricValue: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  metricSubtext: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-tertiary)',
    marginTop: theme.spacing[1],
  },
  tabsContainer: {
    display: 'flex',
    gap: theme.spacing[1],
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[1],
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing[6],
    overflowX: 'auto',
  },
  tabButton: {
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    borderRadius: theme.borderRadius.md,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.sm,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    boxShadow: theme.shadows.sm,
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing[4],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: theme.spacing[6],
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing[3]} 0`,
    borderBottom: '1px solid var(--border-primary)',
  },
  infoLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
  },
  infoValue: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: 'var(--text-primary)',
  },
  card: {
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
  },
  cardItem: {
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
    marginBottom: theme.spacing[3],
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[3],
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    fontWeight: 'bold',
    fontSize: theme.typography.fontSize.lg,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing[3],
    fontSize: theme.typography.fontSize.sm,
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badgeBlue: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  parameterBadge: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--bg-tertiary)',
    padding: '4px 8px',
    borderRadius: theme.borderRadius.md,
    border: '1px solid var(--border-primary)',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing[8],
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
  authPrompt: {
    textAlign: 'center',
    padding: theme.spacing[16],
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius['2xl'],
    boxShadow: theme.shadows.lg,
    maxWidth: '600px',
    margin: '0 auto',
  },
  authTitle: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[4],
  },
  authMessage: {
    fontSize: theme.typography.fontSize.lg,
    color: 'var(--text-secondary)',
    lineHeight: theme.typography.lineHeight.relaxed,
  },
};

export default AgenticAIDashboard;